import React, { useState } from 'react';
import type { BrandedFare, BrandedFareFeature } from '../../services/tourvisioService';

interface BrandedFareSelectorProps {
    outboundFares: BrandedFare[];
    returnFares: BrandedFare[];
    outboundAirline?: string;
    returnAirline?: string;
    outboundRoute?: string;
    returnRoute?: string;
    selectedOutboundFare: BrandedFare | null;
    selectedReturnFare: BrandedFare | null;
    onSelectOutboundFare: (fare: BrandedFare) => void;
    onSelectReturnFare: (fare: BrandedFare) => void;
    language: 'en' | 'sv' | 'da';
    currency?: string;
}

// Service group icons
const getFeatureIcon = (serviceGroup: number, pricingType: number): string => {
    if (pricingType === 2) return '⛔';  // NOT_OFFERED
    if (pricingType === 1) return '💰';  // CHARGEABLE
    return '✅';                          // FREE / INCLUDED
};

// Service group label translations
const SERVICE_GROUP_SV: Record<number, string> = {
    0: 'Biljettyp',
    1: 'Bagage',
    2: 'Bonusprogram',
    3: 'Underhållning',
    4: 'Lounge',
    5: 'Måltid & Dryck',
    6: 'Husdjur',
    7: 'Uppgraderingar'
};

const SERVICE_GROUP_EN: Record<number, string> = {
    0: 'Fare Type',
    1: 'Baggage',
    2: 'Frequent Flyer',
    3: 'Entertainment',
    4: 'Lounge Access',
    5: 'Meal & Beverage',
    6: 'Pets',
    7: 'Upgrades'
};

const SERVICE_GROUP_DA: Record<number, string> = {
    0: 'Billettype',
    1: 'Bagage',
    2: 'Bonusprogram',
    3: 'Underholdning',
    4: 'Lounge',
    5: 'Måltid & Drikke',
    6: 'Kæledyr',
    7: 'Opgraderinger'
};

// Fare tier colors
const FARE_COLORS: Record<string, { bg: string; border: string; accent: string; badge: string }> = {
    ecofly: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-300 dark:border-emerald-700', accent: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300' },
    extrafly: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-300 dark:border-blue-700', accent: 'text-blue-600 dark:text-blue-400', badge: 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300' },
    flexfly: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-300 dark:border-amber-700', accent: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300' },
    primefly: { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-300 dark:border-purple-700', accent: 'text-purple-600 dark:text-purple-400', badge: 'bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300' },
    default: { bg: 'bg-gray-50 dark:bg-gray-800/50', border: 'border-gray-300 dark:border-gray-700', accent: 'text-gray-600 dark:text-gray-400', badge: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' }
};

const getFareColor = (name: string) => {
    const key = name.toLowerCase().replace(/\s+/g, '');
    return FARE_COLORS[key] || FARE_COLORS.default;
};

const BrandedFareSelector: React.FC<BrandedFareSelectorProps> = ({
    outboundFares,
    returnFares,
    outboundAirline,
    returnAirline,
    outboundRoute,
    returnRoute,
    selectedOutboundFare,
    selectedReturnFare,
    onSelectOutboundFare,
    onSelectReturnFare,
    language,
    currency = 'EUR'
}) => {
    const hasOutbound = outboundFares.length > 0;
    const hasReturn = returnFares.length > 0;

    if (!hasOutbound && !hasReturn) return null;

    const labels = language === 'sv' ? SERVICE_GROUP_SV : language === 'da' ? SERVICE_GROUP_DA : SERVICE_GROUP_EN;

    const renderFareCard = (
        fare: BrandedFare,
        isSelected: boolean,
        onClick: () => void,
        isFirst: boolean
    ) => {
        const colors = getFareColor(fare.name);

        return (
            <div
                key={fare.name}
                onClick={onClick}
                className={`
                    relative flex-1 min-w-[160px] max-w-[220px] rounded-xl border-2 cursor-pointer
                    transition-all duration-300 overflow-hidden
                    ${isSelected
                        ? `${colors.border} ${colors.bg} shadow-lg scale-[1.02]`
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600'}
                `}
            >
                {/* Header */}
                <div className={`px-4 py-3 ${isSelected ? colors.bg : 'bg-gray-50 dark:bg-gray-800'} border-b ${isSelected ? colors.border : 'border-gray-100 dark:border-gray-700'}`}>
                    <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-black uppercase tracking-wider ${isSelected ? colors.accent : 'text-gray-700 dark:text-gray-300'}`}>
                            {fare.name}
                        </span>
                        {/* Radio dot */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                            ${isSelected ? `${colors.border.replace('border-', 'border-')} bg-current ${colors.accent}` : 'border-gray-300 dark:border-gray-600'}`}>
                            {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                        </div>
                    </div>
                    {isFirst && (
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${colors.badge} px-2 py-0.5 rounded-full`}>
                            {language === 'sv' ? 'Ingår' : language === 'da' ? 'Inkluderet' : 'Included'}
                        </span>
                    )}
                </div>

                {/* Features */}
                <div className="px-4 py-3 space-y-2">
                    {fare.features.length > 0 ? (
                        fare.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                                <span className="text-xs mt-0.5 flex-shrink-0">
                                    {getFeatureIcon(feature.serviceGroup, feature.pricingType)}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <span className="text-xs text-gray-600 dark:text-gray-400 leading-tight block truncate" title={feature.label}>
                                        {feature.label}
                                    </span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                            {language === 'sv' ? 'Standardvillkor' : language === 'da' ? 'Standardvilkår' : 'Standard terms'}
                        </p>
                    )}
                </div>

                {/* Price footer */}
                <div className={`px-4 py-3 border-t ${isSelected ? colors.border : 'border-gray-100 dark:border-gray-700'} ${isSelected ? colors.bg : 'bg-gray-50/50 dark:bg-gray-800/50'}`}>
                    <div className={`text-center font-bold ${fare.priceDifference > 0 ? 'text-orange-600 dark:text-orange-400' : `${colors.accent}`}`}>
                        {fare.priceDifference > 0
                            ? `+${Math.round(fare.priceDifference)} ${currency}`
                            : (language === 'sv' ? '0 SEK' : language === 'da' ? '0 DKK' : `0 ${currency}`)}
                    </div>
                </div>
            </div>
        );
    };

    const renderLegSection = (
        fares: BrandedFare[],
        selectedFare: BrandedFare | null,
        onSelect: (fare: BrandedFare) => void,
        airline: string | undefined,
        route: string | undefined,
        legLabel: string,
        icon: string
    ) => {
        if (fares.length === 0) return null;

        return (
            <div className="mb-6">
                {/* Leg header */}
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-base">{icon}</span>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                            {legLabel}
                        </h4>
                        {(airline || route) && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                {airline}{route ? ` • ${route}` : ''}
                            </p>
                        )}
                    </div>
                </div>

                {/* Fare cards row */}
                <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                    {fares.map((fare, idx) =>
                        renderFareCard(
                            fare,
                            selectedFare?.name === fare.name,
                            () => onSelect(fare),
                            idx === 0
                        )
                    )}
                </div>
            </div>
        );
    };

    // Calculate total extra cost
    const totalExtra = (selectedOutboundFare?.priceDifference || 0) + (selectedReturnFare?.priceDifference || 0);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 mb-8">
            {/* Section Title */}
            <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight mb-6 flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                </svg>
                {language === 'sv' ? 'Välj Biljettklass' : language === 'da' ? 'Vælg Billetklasse' : 'Select Fare Class'}
            </h3>

            {/* Pricing type legend */}
            <div className="flex flex-wrap gap-4 mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                    <span>✅</span>
                    <span className="font-medium">{language === 'sv' ? 'Ingår' : language === 'da' ? 'Inkluderet' : 'Included'}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                    <span>💰</span>
                    <span className="font-medium">{language === 'sv' ? 'Mot avgift' : language === 'da' ? 'Mod gebyr' : 'Chargeable'}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                    <span>⛔</span>
                    <span className="font-medium">{language === 'sv' ? 'Ej tillgängligt' : language === 'da' ? 'Ikke tilgængelig' : 'Not available'}</span>
                </div>
            </div>

            {/* Outbound fares */}
            {renderLegSection(
                outboundFares,
                selectedOutboundFare,
                onSelectOutboundFare,
                outboundAirline,
                outboundRoute,
                language === 'sv' ? 'Utresa' : language === 'da' ? 'Udrejse' : 'Outbound',
                '🛫'
            )}

            {/* Return fares */}
            {renderLegSection(
                returnFares,
                selectedReturnFare,
                onSelectReturnFare,
                returnAirline,
                returnRoute,
                language === 'sv' ? 'Hemresa' : language === 'da' ? 'Hjemrejse' : 'Return',
                '🛬'
            )}

            {/* Total supplement */}
            {totalExtra > 0 && (
                <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg flex items-center justify-between border border-orange-200 dark:border-orange-800">
                    <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
                        {language === 'sv' ? 'Extra kostnad för vald biljettklass:' : language === 'da' ? 'Ekstra omkostning for valgt billetklasse:' : 'Fare class supplement:'}
                    </span>
                    <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                        + {Math.round(totalExtra)} {currency}
                    </span>
                </div>
            )}
        </div>
    );
};

export default BrandedFareSelector;
